'use client';

import {
   type Country,
   type CountryCode,
   type UserLocaleCode,
   countryByCode,
   parseUserLocale,
} from '@ifixit/i18n/user-locale';
import { useCookieState } from './use-cookie-state';
import { useMemo } from 'react';

/**
 * Detects the user's locale from geo headers (via middleware cookie) and
 * browser language preferences. Returns null when the country is unknown.
 */
export function useDetectedUserLocale(): UserLocaleCode | null {
   const [countryCode] = useCookieState('detected-country');

   return useMemo(() => {
      const normalizedCountryCode = normalizeCountryCode(countryCode);
      if (!normalizedCountryCode) return null;
      const country = countryByCode[normalizedCountryCode as CountryCode];
      if (!country) return null;
      return matchBrowserLanguage(country);
   }, [countryCode]);
}

/**
 * Match the browser's language preferences against a country's available
 * locales. Falls back to the country's first locale if no match is found.
 */
function matchBrowserLanguage(country: Country): UserLocaleCode {
   const browserLanguages = typeof navigator === 'undefined' ? [] : navigator.languages;

   for (const browserLang of browserLanguages) {
      // Try exact match first (e.g. "de-DE" matches "de-DE")
      const exactMatch = country.locales.find(
         locale => locale.toLowerCase() === browserLang.toLowerCase()
      );
      if (exactMatch) return exactMatch;

      // Try language-only match (e.g. "de" matches "de-DE")
      const langPrefix = browserLang.split('-')[0].toLowerCase();
      const prefixMatch = country.locales.find(
         locale => parseUserLocale(locale).language.toLowerCase() === langPrefix
      );
      if (prefixMatch) return prefixMatch;
   }

   return country.locales[0];
}

function normalizeCountryCode(countryCode: string | null | undefined): string | null {
   if (countryCode == null) {
      return null;
   }

   const normalized = countryCode.trim().toUpperCase();
   return normalized === '' ? null : normalized;
}
